import { css } from '@emotion/react';
import { theme } from '@frontend/theme';

type LiStyleProps = {
  disabled?: boolean;
  active?: boolean;
  destructive?: boolean;
  darkMode?: boolean;
  preserveCasing?: boolean;
};

const menuDividerStyle = ({ color }: { color?: string }) => css`
  margin-top: ${theme.spacing(0.5)};
  margin-bottom: ${theme.spacing(0.5)};
  border-top: 1px solid ${color || theme.colors.neutral20};
`;

const menuItemStyle = (props?: LiStyleProps) => [
  css`
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    height: 40px;
    min-height: 40px;
    justify-content: flex-start;
    margin: 0;
    outline: none;
    padding: ${theme.spacing(0, 2)};
    gap: ${theme.spacing(1)};
    position: relative;
    text-decoration: none;
    transition: background-color 250ms ease-out;
    ${props?.darkMode &&
    `
      color: ${theme.colors.white};
      fill: ${theme.colors.white};
    `}

    ${!props?.preserveCasing &&
    `
      > :first-letter {
        text-transform: uppercase;
      }
      
      `}

    span {
      color: inherit;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      display: inline;
      text-align: start;
    }

    :hover {
      background-color: ${props?.darkMode ? theme.colors.neutral80 : theme.colors.neutral5};
    }
    :focus {
      background-color: ${props?.darkMode ? theme.colors.neutral80 : theme.colors.neutral5};
      outline: none;
    }
  `,

  !props?.destructive &&
    css`
      svg {
        /* fill needs to be here in case the svg is inside a :visited <a> tag */
        fill: inherit;
      }
    `,
  props?.disabled &&
    css`
      color: black;
      cursor: default;
      pointer-events: none;
      :hover,
      :focus {
        outline: none;
      }
      span {
        color: ${props.darkMode ? theme.colors.neutral60 : theme.colors.status.disabled};
      }
      ,
      svg {
        fill: ${props.darkMode ? theme.colors.neutral60 : theme.colors.status.disabled};
        color: ${props.darkMode ? theme.colors.neutral60 : theme.colors.status.disabled};
      }
    `,
  props?.active &&
    css`
      background-color: ${props?.darkMode ? theme.colors.neutral80 : theme.colors.neutral5};
      outline: none;
      ::before {
        content: '';
        background: ${theme.colors.primary50};
        height: 100%;
        width: 3px;
        left: 0;
        position: absolute;
      }
    `,
];

const menuItemDropdownStyle = (props?: LiStyleProps) => {
  return css([
    menuItemStyle(props),
    css`
      flex-shrink: 0;
      column-gap: ${theme.spacing(1)};
    `,
  ]);
};

const menuStyle = (darkMode: boolean) => css`
  background: ${darkMode ? theme.colors.neutral90 : theme.colors.white};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.heavy};
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  min-width: 100px;
  overflow: auto;
  padding: ${theme.spacing(1, 0)};
  z-index: ${theme.zIndex.popover};

  :focus {
    outline: none;
  }

  h2 {
    border-left: 2px solid transparent;
    font-size: ${theme.fontSize(18)};
    margin: 0;
    padding: ${theme.spacing(1, 2)};
    text-transform: capitalize;
  }
`;

export const PopoverMenuStyles = {
  menuItem: (props: LiStyleProps) => menuItemStyle(props),
  menuItemDropdown: (props: LiStyleProps) => menuItemDropdownStyle(props),
  menu: (darkMode: boolean) => menuStyle(darkMode),
  divider: ({ color }: { color?: string }) => menuDividerStyle({ color }),
};
